/**
 * Operator portal PR 3 (migration 0206) — which haulers' data this operator sees in its portal
 * and through its API keys. Paelode decides; the hauler's own customer mapping is the other half.
 *
 * An operator sees a hauler's data only when BOTH hold:
 *   - the hauler maps a customer to this operator (the hauler's setting, shown, not editable here);
 *   - Paelode gave access here.
 * So each row shows both halves and the result. "Give access" / "Remove access" change only
 * Paelode's half; removing keeps a record of who and when.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { Pill, labelStyle, subText } from '../../ui/bits';
import { useTenants } from '../tenants/hooks';
import {
  useGrantHaulerAccess,
  useOperatorHaulerAccess,
  useRevokeHaulerAccess,
  useOperatorHaulerSuggestions,
  useDecideSuggestion,
  useRecomputeSuggestions,
  describeEvidence,
  type OperatorHaulerAccessRow,
  type OperatorHaulerSuggestion,
} from './hauler-access-hooks';

/**
 * One OPEN match-up suggestion (operator portal PR 4, migration 0211): the score, the evidence in
 * words, and Confirm (gives access, source SUGGESTION) / Dismiss (not suggested again unless the
 * score rises by 0.05 or more).
 */
function SuggestionRow({
  operatorId,
  s,
}: {
  operatorId: string;
  s: OperatorHaulerSuggestion;
}): React.JSX.Element {
  const confirm = useDecideSuggestion(operatorId, s.id, 'confirm');
  const dismiss = useDecideSuggestion(operatorId, s.id, 'dismiss');
  const pending = confirm.isPending || dismiss.isPending;
  const done = (msg: string) => ({
    onSuccess: () => toast.success(msg),
    onError: () => toast.error('Could not save. Try again.'),
  });
  return (
    <div
      data-testid={`hauler-suggestion-${s.haulerTenantId}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 0',
        borderBottom: '1px solid var(--color-border-secondary)',
        fontSize: '14px',
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontWeight: 500 }}>{s.haulerName}</span>
      <Pill label={`${String(Math.round(s.score * 100))}% match`} tone="accent" />
      <span style={{ ...labelStyle, textTransform: 'none', marginBottom: 0 }}>
        {describeEvidence(s.evidence).join(' · ')}
      </span>
      <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
        <Button
          type="button"
          disabled={pending}
          onClick={(): void => confirm.mutate({}, done('Access given'))}
        >
          Confirm
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={(): void => dismiss.mutate({}, done('Suggestion dismissed'))}
        >
          Dismiss
        </Button>
      </span>
    </div>
  );
}

function accessPill(row: OperatorHaulerAccessRow): React.JSX.Element {
  if (row.visible) return <Pill label="Sees this hauler" tone="accent" />;
  if (row.accessStatus === 'ACTIVE')
    return <Pill label="Access on · no customer mapping yet" tone="neutral" />;
  if (row.accessStatus === 'REVOKED') return <Pill label="Access removed" tone="muted" />;
  return <Pill label="Not confirmed" tone="muted" />;
}

export function OperatorHaulersCard({
  platformOperatorId,
}: {
  platformOperatorId: string;
}): React.JSX.Element {
  const { data, isLoading, isError } = useOperatorHaulerAccess(platformOperatorId);
  const grant = useGrantHaulerAccess(platformOperatorId);
  const revoke = useRevokeHaulerAccess(platformOperatorId);
  const allHaulers = useTenants('active', 'name');
  const suggestions = useOperatorHaulerSuggestions(platformOperatorId);
  const recompute = useRecomputeSuggestions(platformOperatorId);
  const [toAdd, setToAdd] = React.useState('');
  const pending = grant.isPending || revoke.isPending;

  const listed = new Set((data?.haulers ?? []).map((h) => h.haulerTenantId));
  const addable = (allHaulers.data?.tenants ?? []).filter((t) => !listed.has(t.id));

  const change = (haulerTenantId: string, give: boolean): void => {
    const body = { platformOperatorId, haulerTenantId };
    const opts = {
      onSuccess: () => toast.success(give ? 'Access given' : 'Access removed'),
      onError: () => toast.error('Could not save. Try again.'),
    };
    if (give) grant.mutate(body, opts);
    else revoke.mutate(body, opts);
  };

  return (
    <section
      data-testid="operator-haulers-card"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 6px' }}>
        Portal: haulers this operator sees
      </h3>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 14px' }}>
        The operator sees a hauler&apos;s tickets, volumes and tanks only when the hauler maps a
        customer to this operator <strong>and</strong> access is given here.
      </p>

      {/* Operator portal PR 4: haulers the match-up suggests (score of 90% or more). */}
      <div data-testid="hauler-suggestions" style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h4 style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Suggested by match-up</h4>
          <Button
            type="button"
            variant="outline"
            disabled={recompute.isPending}
            onClick={(): void =>
              recompute.mutate(
                { platformOperatorId },
                {
                  onSuccess: () => toast.success('Suggestions recomputed'),
                  onError: (err: unknown) =>
                    toast.error(
                      // 0211 (Session 2's review, M3): a second recompute is refused at once.
                      (err as { code?: unknown } | null)?.code === 'SUGGESTION_RECOMPUTE_RUNNING'
                        ? 'A recompute is already running. Try again in a minute.'
                        : 'Could not recompute. Try again.',
                    ),
                },
              )
            }
          >
            Recompute now
          </Button>
        </div>
        {(suggestions.data?.suggestions ?? []).length === 0 ? (
          <p style={{ ...subText, textTransform: 'none', margin: '6px 0 0' }}>
            No suggestions. A hauler is suggested at a 90% match or more, from its customer mapping
            and its loads at this operator&apos;s wells.
          </p>
        ) : (
          (suggestions.data?.suggestions ?? []).map((s) => (
            <SuggestionRow key={s.id} operatorId={platformOperatorId} s={s} />
          ))
        )}
      </div>

      {isLoading && <p style={{ ...subText, textTransform: 'none' }}>Loading haulers…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load haulers.
        </p>
      )}
      {data !== undefined && data.haulers.length === 0 && (
        <p style={{ ...subText, textTransform: 'none' }}>
          No hauler maps a customer to this operator yet.
        </p>
      )}
      {data !== undefined && data.haulers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {data.haulers.map((h) => (
            <div
              key={h.haulerTenantId}
              data-testid={`hauler-access-${h.haulerTenantId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 0',
                borderBottom: '1px solid var(--color-border-secondary)',
                fontSize: '14px',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontWeight: 500 }}>{h.haulerName}</span>
              {h.haulerAbbreviation !== null && (
                <Pill label={h.haulerAbbreviation} tone="neutral" />
              )}
              <span style={{ ...labelStyle, textTransform: 'none', marginBottom: 0 }}>
                {h.mapsCustomer
                  ? `Maps a customer (${String(h.activeCustomerRows)} active)`
                  : 'No customer mapped'}
              </span>
              {accessPill(h)}
              <span style={{ marginLeft: 'auto' }}>
                {h.accessStatus === 'ACTIVE' ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending}
                    onClick={(): void => change(h.haulerTenantId, false)}
                  >
                    Remove access
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={pending}
                    onClick={(): void => change(h.haulerTenantId, true)}
                  >
                    Give access
                  </Button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '14px' }}>
        <label
          htmlFor="add-hauler-access"
          style={{ ...labelStyle, textTransform: 'none', marginBottom: 0 }}
        >
          Add another hauler
        </label>
        <select
          id="add-hauler-access"
          value={toAdd}
          onChange={(e): void => setToAdd(e.target.value)}
          style={{ minWidth: '220px' }}
        >
          <option value="">Choose a hauler…</option>
          {addable.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.haulerAbbreviation !== null ? ` (${t.haulerAbbreviation})` : ''}
            </option>
          ))}
        </select>
        <Button
          type="button"
          disabled={pending || toAdd === ''}
          onClick={(): void => {
            change(toAdd, true);
            setToAdd('');
          }}
        >
          Give access
        </Button>
      </div>
    </section>
  );
}
